import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	buildAssignedTrainerRows,
	buildTrainingTrainerUpdatePayload,
	enrichAssignedTrainerRows,
	normalizeAssignedTrainers,
	parseTrainingDurationHours,
	toAssignedTrainerApiItem,
} from '../trainerDetailsHelpers'
import UserService2 from '@/services/UserService2'

const hasTrainingCoordinates = training => {
	const g_lat = training?.g_lat
	const g_long = training?.g_long
	return g_lat !== '' && g_lat != null && g_long !== '' && g_long != null
}

const mapUserToTrainer = user => {
	if (!user) return null

	return {
		...user,
		_id: user._id || user.id,
		name: user.name || user.nazwa || '',
		city: `${user.miasto ?? user.city ?? ''}`.trim(),
		defaultHourlyRateGross: `${user.stawka_godzinowa ?? user.defaultHourlyRateGross ?? ''}`.trim(),
		certificationIds: Array.isArray(user.certificationIds) ? user.certificationIds : [],
		distanceKm: user.distanceKm ?? user.distanceInKm ?? null,
	}
}

const mergeTrainerDirectory = async (training, apiTrainers) => {
	const merged = Array.isArray(apiTrainers) ? [...apiTrainers] : []
	const existingIds = new Set(merged.map(item => String(item?._id || item?.id || '')).filter(Boolean))
	const assignedTrainerIds = normalizeAssignedTrainers(training)
		.map(item => item.trainerId)
		.filter(Boolean)
	const missingTrainerIds = assignedTrainerIds.filter(trainerId => !existingIds.has(String(trainerId)))

	if (!missingTrainerIds.length) return merged

	const detailResults = await Promise.all(
		missingTrainerIds.map(trainerId => UserService2.getById(trainerId))
	)

	detailResults.forEach(result => {
		const mappedTrainer = mapUserToTrainer(result.data)
		if (!mappedTrainer?._id) return
		merged.push(mappedTrainer)
	})

	return merged
}

export default function useAssignedTrainers({
	training,
	trainingId,
	getTrainersForTraining,
	updateTraining,
	refreshTraining,
}) {
	const { t } = useTranslation()
	const [trainerDirectory, setTrainerDirectory] = useState([])

	const loadTrainerDirectory = useCallback(async trainingData => {
		if (!hasTrainingCoordinates(trainingData)) {
			return {
				data: [],
				error: t('trainings.details.messages.trainingLocationRequired'),
			}
		}

		return getTrainersForTraining({
			g_lat: trainingData.g_lat,
			g_long: trainingData.g_long,
			requiredCertificationIds: Array.isArray(trainingData?.requiredCertificationIds)
				? trainingData.requiredCertificationIds
				: [],
		})
	}, [getTrainersForTraining, t])

	const syncTrainerDirectory = useCallback(async trainingData => {
		const trainersResult = await loadTrainerDirectory(trainingData)
		const apiTrainers = trainersResult.error ? [] : (Array.isArray(trainersResult.data) ? trainersResult.data : [])
		const mergedTrainers = await mergeTrainerDirectory(trainingData, apiTrainers)
		setTrainerDirectory(mergedTrainers)
		return {
			success: !trainersResult.error,
			error: trainersResult.error || null,
			data: mergedTrainers,
		}
	}, [loadTrainerDirectory])

	const assignedTrainerRows = useMemo(
		() => enrichAssignedTrainerRows(buildAssignedTrainerRows(training, trainerDirectory), training),
		[training, trainerDirectory]
	)

	const persistAssignedTrainers = useCallback(async nextTrainerRows => {
		if (!training) {
			return { success: false, error: t('trainings.details.messages.loadFailed') }
		}

		const result = await updateTraining(
			trainingId,
			buildTrainingTrainerUpdatePayload(training, nextTrainerRows)
		)

		if (result.success) {
			await refreshTraining()
			await syncTrainerDirectory(result.data || training)
		}

		return result
	}, [refreshTraining, syncTrainerDirectory, t, training, trainingId, updateTraining])

	const addAssignedTrainer = useCallback(async newTrainer => {
		const nextTrainerRows = [
			...assignedTrainerRows,
			{
				key: newTrainer.trainerId || newTrainer.trainerName,
				trainerId: newTrainer.trainerId || null,
				trainerName: newTrainer.trainerName || '',
				miasto: newTrainer.miasto || newTrainer.trainerCity || '',
				odleglosc_km: newTrainer.distanceKm ?? newTrainer.odleglosc_km ?? '',
				stawka_godzinowa: newTrainer.defaultHourlyRateGross ?? newTrainer.stawka_godzinowa ?? '',
				stawka_za_kilometr: newTrainer.stawka_za_kilometr ?? 1,
				ilosc_godzin: newTrainer.ilosc_godzin ?? parseTrainingDurationHours(training) ?? '',
				rozliczono: false,
				inne_koszty: [],
			},
		]

		return persistAssignedTrainers(nextTrainerRows)
	}, [assignedTrainerRows, persistAssignedTrainers, training])

	const removeAssignedTrainer = useCallback(async trainerRow => {
		const nextTrainerRows = assignedTrainerRows.filter(item => item.key !== trainerRow.key)
		return persistAssignedTrainers(nextTrainerRows)
	}, [assignedTrainerRows, persistAssignedTrainers])

	const refreshTrainersForModal = useCallback(async () => {
		if (!training) {
			return { success: false, error: t('trainings.details.messages.loadFailed'), data: [] }
		}

		return syncTrainerDirectory(training)
	}, [syncTrainerDirectory, t, training])

	return {
		assignedTrainerRows,
		assignedTrainers: assignedTrainerRows.map(row => toAssignedTrainerApiItem(row, training)),
		addAssignedTrainer,
		persistAssignedTrainers,
		removeAssignedTrainer,
		refreshTrainersForModal,
		syncTrainerDirectory,
		trainerDirectory,
	}
}
